import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { HasMark } from "@/components/BrandText";
import { SERVICES, IMAGES } from "@/data/site";

export default function Services() {
  return (
    <div data-testid="page-services" className="pt-32">
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 md:pt-20 md:pb-20">
          <Reveal>
            <div className="overline mb-5">Consulting Practices</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl md:text-7xl text-white leading-[1.05] tracking-tight max-w-5xl">
              Comprehensive <span className="italic gold-gradient-text">Consulting Solutions</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-3xl text-white/70 text-lg leading-relaxed">
              Seven specialised practices, one strategic compass — engineered for measurable results across
              Dubai, India and international markets.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Service quick index */}
      <section className="relative pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`service-index-${s.id}`}
                className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 border border-white/10 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors"
              >
                {String(i + 1).padStart(2, "0")} · {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed alternating service blocks */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reversed = i % 2 === 1;
            return (
              <div id={s.id} key={s.id} className="scroll-mt-28">
                <Stagger className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center`}>
                  <Item className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
                    <div className="overline mb-4">Practice · {String(i+1).padStart(2,"0")}</div>
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37]">
                        <Icon size={22} strokeWidth={1.5} />
                      </div>
                      <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight">
                        {s.title}
                      </h2>
                    </div>
                    <p className="mt-6 text-white/70 leading-relaxed max-w-2xl">{s.summary}</p>
                    <ul className="mt-7 grid sm:grid-cols-2 gap-y-3 gap-x-6">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-[14px] text-white/80">
                          <Check size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-9">
                      <Link to="/contact" className="btn-ghost" data-testid={`service-cta-${s.id}`}>
                        Discuss This Practice <ArrowRight size={16} />
                      </Link>
                    </div>
                  </Item>
                  <Item className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}>
                    <div className="relative">
                      <div className="absolute -top-3 -left-3 w-20 h-20 border-l border-t border-[#D4AF37]/60" />
                      <div className="absolute -bottom-3 -right-3 w-20 h-20 border-r border-b border-[#D4AF37]/60" />
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src={i % 2 === 0 ? IMAGES.meeting : IMAGES.boardroom}
                          alt={s.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-[#0B3D2E]/40" />
                        <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
                          <span className="font-display text-white/90 text-2xl">{String(i+1).padStart(2,"0")}</span>
                          <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Strategic · Practical · Measurable</span>
                        </div>
                      </div>
                    </div>
                  </Item>
                </Stagger>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 bg-[#0c0c0c] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <SectionLabel
            align="center"
            overline={<>Engage <HasMark /></>}
            title="Let's Build Your Next Growth Chapter"
            subtitle="Tell us about your market, your challenge, your ambition. We will respond with a tailored point of view."
            className="mx-auto"
          />
          <div className="mt-10">
            <Link to="/contact" className="btn-gold" data-testid="services-bottom-cta">
              Book A Consultation <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
